"use client";

export type LtiStorageResult = "supported" | "unsupported" | "timeout" | "error";

export const LTI_EMBEDDED_SESSION_KEY = "studystash:lti-embedded-session";

const PROBE_KEY_PREFIX = "studystash:lti-storage-probe";
const PROBE_VALUE = "ok";
const DEFAULT_TIMEOUT_MS = 1200;

function is_in_iframe(): boolean {
	try {
		return window.self !== window.top;
	} catch {
		return true;
	}
}

function get_lti_storage_target(): Window | null {
	const params = new URLSearchParams(window.location.search);
	const target_name = params.get("lti_storage_target");

	if (target_name && window.parent?.frames?.[target_name]) {
		return window.parent.frames[target_name];
	}

	return window.parent && window.parent !== window ? window.parent : null;
}

function random_id(): string {
	if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
	return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function send_lti_platform_message<T>(
	params: {
		key: string;
		subject: "lti.put_data" | "lti.get_data";
		response_subject: "lti.put_data.response" | "lti.get_data.response";
		value?: string;
		timeout_ms?: number;
		resolve_response: (data: Record<string, unknown>) => T;
		timeout_value: T;
	}
): Promise<T> {
	if (!is_in_iframe()) return Promise.resolve(params.timeout_value);

	const target = get_lti_storage_target();
	if (!target) return Promise.resolve(params.timeout_value);

	const storage_target = target;
	const message_id = random_id();

	return new Promise((resolve) => {
		let resolved = false;
		const timeout = window.setTimeout(() => finish(params.timeout_value), params.timeout_ms ?? DEFAULT_TIMEOUT_MS);

		function finish(result: T) {
			if (resolved) return;
			resolved = true;
			window.clearTimeout(timeout);
			window.removeEventListener("message", on_message);
			resolve(result);
		}

		function on_message(event: MessageEvent) {
			if (event.source !== storage_target) return;
			const data = event.data;
			if (!data || typeof data !== "object") return;
			const response = data as Record<string, unknown>;

			if (typeof response.message_id === "string" && response.message_id !== message_id) return;
			if (typeof response.key === "string" && response.key !== params.key) return;
			if (response.subject !== params.response_subject) return;

			finish(params.resolve_response(response));
		}

		window.addEventListener("message", on_message);

		try {
			storage_target.postMessage({
				subject: params.subject,
				message_id,
				key: params.key,
				...(params.value !== undefined ? { value: params.value } : {}),
			}, "*");
		} catch {
			finish(params.timeout_value);
		}
	});
}

export async function put_lti_platform_data(key: string, value: string, timeout_ms = DEFAULT_TIMEOUT_MS): Promise<boolean> {
	return send_lti_platform_message({
		key,
		value,
		timeout_ms,
		subject: "lti.put_data",
		response_subject: "lti.put_data.response",
		timeout_value: false,
		resolve_response: (data) => !data.error,
	});
}

export async function get_lti_platform_data(key: string, timeout_ms = DEFAULT_TIMEOUT_MS): Promise<string | null> {
	return send_lti_platform_message({
		key,
		timeout_ms,
		subject: "lti.get_data",
		response_subject: "lti.get_data.response",
		timeout_value: null,
		resolve_response: (data) => typeof data.value === "string" ? data.value : null,
	});
}

export function put_lti_session_storage(key: string, value: string): boolean {
	try {
		window.sessionStorage.setItem(key, value);
		return true;
	} catch {
		return false;
	}
}

export function get_lti_session_storage(key: string): string | null {
	try {
		return window.sessionStorage.getItem(key);
	} catch {
		return null;
	}
}

let token_responder_installed = false;

export function install_lti_auth_token_responder(): void {
	if (token_responder_installed || !("serviceWorker" in navigator)) return;
	token_responder_installed = true;

	navigator.serviceWorker.addEventListener("message", async (event) => {
		if (event.data?.type !== "GET_LTI_ACCESS_TOKEN") return;

		const port = event.ports?.[0];
		if (!port) return;

		const access_token =
			await get_lti_platform_data(LTI_EMBEDDED_SESSION_KEY) ??
			get_lti_session_storage(LTI_EMBEDDED_SESSION_KEY);

		port.postMessage({
			ok: typeof access_token === "string" && access_token.length > 0,
			accessToken: access_token,
		});
	});
}

export async function probe_lti_platform_storage(): Promise<LtiStorageResult> {
	const key = `${PROBE_KEY_PREFIX}:${random_id()}`;
	const did_put = await put_lti_platform_data(key, PROBE_VALUE);
	if (!did_put) return "unsupported";

	const value = await get_lti_platform_data(key);
	return value === PROBE_VALUE ? "supported" : "timeout";
}

function wait_for_controller(): Promise<ServiceWorker | null> {
	if (navigator.serviceWorker.controller) return Promise.resolve(navigator.serviceWorker.controller);

	return new Promise((resolve) => {
		const timeout = window.setTimeout(() => resolve(navigator.serviceWorker.controller), 1000);
		navigator.serviceWorker.addEventListener("controllerchange", () => {
			window.clearTimeout(timeout);
			resolve(navigator.serviceWorker.controller);
		}, { once: true });
	});
}

function send_service_worker_message(worker: ServiceWorker, message: Record<string, unknown>): Promise<boolean> {
	return new Promise((resolve) => {
		const channel = new MessageChannel();
		const timeout = window.setTimeout(() => resolve(false), 1000);

		channel.port1.onmessage = (event) => {
			window.clearTimeout(timeout);
			resolve(Boolean(event.data?.ok));
		};

		worker.postMessage(message, [channel.port2]);
	});
}

function post_token_to_service_worker(worker: ServiceWorker, access_token: string): Promise<boolean> {
	return send_service_worker_message(worker, {
		type: "SET_LTI_ACCESS_TOKEN",
		accessToken: access_token,
	});
}

function clear_token_from_service_worker(worker: ServiceWorker): Promise<boolean> {
	return send_service_worker_message(worker, { type: "CLEAR_LTI_ACCESS_TOKEN" });
}

export async function install_lti_auth_service_worker(access_token: string): Promise<boolean> {
	if (!("serviceWorker" in navigator)) return false;
	install_lti_auth_token_responder();

	try {
		const registration = await navigator.serviceWorker.register("/lti-auth-sw.js", { scope: "/" });
		await navigator.serviceWorker.ready;

		const worker =
			registration.active ??
			registration.waiting ??
			registration.installing ??
			await wait_for_controller();

		if (!worker) return false;

		const did_post = await post_token_to_service_worker(worker, access_token);
		if (did_post) return true;

		const controller = await wait_for_controller();
		return controller ? post_token_to_service_worker(controller, access_token) : false;
	} catch {
		return false;
	}
}

export async function clear_lti_auth_service_worker(): Promise<boolean> {
	if (!("serviceWorker" in navigator)) return false;

	try {
		const registration = await navigator.serviceWorker.getRegistration("/");
		const worker =
			navigator.serviceWorker.controller ??
			registration?.active ??
			registration?.waiting ??
			registration?.installing ??
			await wait_for_controller();

		return worker ? clear_token_from_service_worker(worker) : false;
	} catch {
		return false;
	}
}
